import { http } from './http'

const base = import.meta.env.VITE_API_BASE_URL ?? ''

/**
 * ============================================================
 * ENDPOINTS
 * ============================================================
 */

export const hemocultureEndpoints = {
  login: () => `${base}/api/v1/auth/login`,
  logout: () => `${base}/api/v1/auth/logout`,
  me: () => `${base}/api/v1/auth/me`,
  updatePassword: () => `${base}/api/v1/auth/password`,

  reports: (params?: Record<string, string | number>) => {
    const q = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : ''

    return `${base}/api/v1/hemoculture-reports${q}`
  },

  report: (id: number) => `${base}/api/v1/hemoculture-reports/${id}`,

  reportSubmit: (id: number) => `${base}/api/v1/hemoculture-reports/${id}/submit`,

  reportValidate: (id: number) => `${base}/api/v1/hemoculture-reports/${id}/validate`,

  reportReject: (id: number) => `${base}/api/v1/hemoculture-reports/${id}/reject`,

  dashboard: (params?: Record<string, string | number>) => {
    const q = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : ''

    return `${base}/api/v1/dashboard${q}`
  },

  dashboardEvolution: (params?: Record<string, string | number>) => {
    const q = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : ''

    return `${base}/api/v1/dashboard/evolution${q}`
  },

  dashboardComparatif: (params?: Record<string, string | number>) => {
    const q = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : ''

    return `${base}/api/v1/dashboard/comparatif-labos${q}`
  },

  dashboardStatutSoumissions: (params?: Record<string, string | number>) => {
    const q = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : ''

    return `${base}/api/v1/dashboard/statut-soumissions${q}`
  },
}

/**
 * ============================================================
 * UTILISATEUR
 * ============================================================
 */

export interface HemocultureUser {
  id: number
  first_name: string
  last_name: string
  email: string
  is_active: boolean
  is_admin: boolean

  profile: {
    id: number
    name: string
  } | null

  laboratory: {
    id: number
    sigle: string
    full_name: string
    code: string
  } | null
}

/**
 * ============================================================
 * RAPPORT
 * ============================================================
 */

export interface HemocultureReport {
  id: number

  laboratory_id: number

  laboratory: {
    id: number
    sigle: string
    full_name: string
    code: string
  } | null

  submitted_by: number

  submitter: {
    id: number
    name: string
    email: string
  } | null

  report_date: string
  report_month: string

  type_patient: 'Enfants' | 'Adultes'

  nombre_demandes_hemoculture: number
  nombre_echantillons_un_flacon: number
  nombre_flacons_consommes: number
  nombre_resultats_positifs: number
  nombre_resultats_negatifs: number
  nombre_vrais_pathogenes: number
  nombre_resultats_contaminants: number
  nombre_faux_positifs: number
  nombre_souches_conservees: number

  indicateurs: {
    taux_echantillon_un_flacon: number
    pct_pathogenes: number
    pct_contaminants: number
    pct_faux_positifs: number
    pct_souches_conservees: number
  }

  statut: 'brouillon' | 'soumis' | 'valide' | 'rejete'

  commentaire: string | null

  submitted_at: string | null
  created_at: string
  updated_at: string
}

/**
 * ============================================================
 * FORMULAIRE
 * ============================================================
 */

export interface ReportFormData {
  report_date: string
  type_patient: 'Enfants' | 'Adultes'

  nombre_demandes_hemoculture: number
  nombre_echantillons_un_flacon: number
  nombre_flacons_consommes: number

  nombre_resultats_positifs: number
  nombre_resultats_negatifs: number

  nombre_vrais_pathogenes: number
  nombre_resultats_contaminants: number
  nombre_faux_positifs: number

  nombre_souches_conservees: number

  commentaire?: string
}

/**
 * ============================================================
 * DASHBOARD
 * ============================================================
 */

export interface DashboardData {
  annee: number

  totaux: {
    demandes: number
    flacons_consommes: number
    positifs: number
    negatifs: number

    vrais_pathogenes: number
    contaminants: number
    faux_positifs: number
    souches_conservees: number
  }

  indicateurs: {
    taux_positivite: number
    pct_pathogenes: number
    pct_contaminants: number
    pct_faux_positifs: number
    pct_souches_conservees: number
    taux_echantillon_un_flacon: number
  }

  seuils: Record<
    string,
    {
      min?: number
      max?: number
      unite: string
    }
  >
}

/**
 * ============================================================
 * EVOLUTION MENSUELLE
 *
 * IMPORTANT :
 * Les données restent séparées par type_patient.
 * C'est ce qui permet de construire les 4 graphes
 * de ta capture.
 * ============================================================
 */

export interface EvolutionRow {
  mois: number

  type_patient: 'Enfants' | 'Adultes' | string

  demandes: number

  /**
   * Nombre d'échantillons avec un seul flacon.
   */
  echantillons_un_flacon: number

  /**
   * Alias accepté si ton backend utilise encore
   * "echantillons".
   */
  echantillons?: number

  /**
   * Certains endpoints renvoient le champ nommé
   * `nombre_echantillons_un_flacon` (API Laravel resource).
   * Accepter cette propriété évite les erreurs TypeScript
   * et permet d'utiliser la valeur directe de la base.
   */
  nombre_echantillons_un_flacon?: number

  flacons: number

  positifs: number

  negatifs: number

  pathogenes: number

  contaminants: number

  faux_positifs: number

  souches: number

  indicateurs: {
    taux_positivite: number
    pct_pathogenes: number
    pct_contaminants: number
    pct_souches_conservees: number
  }
}

export interface EvolutionData {
  annee: number
  evolution: EvolutionRow[]
}

/**
 * ============================================================
 * API
 * ============================================================
 */

export const hemocultureApi = {
  async login(
    email: string,
    password: string,
  ): Promise<{
    token: string
    user: HemocultureUser
  }> {
    return http(hemocultureEndpoints.login(), {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
      }),
    })
  },

  async logout(): Promise<void> {
    return http(hemocultureEndpoints.logout(), {
      method: 'POST',
    })
  },

  async me(): Promise<{
    user: HemocultureUser
  }> {
    return http(hemocultureEndpoints.me())
  },

  async getReports(params?: Record<string, string | number>): Promise<{
    data: HemocultureReport[]
    meta: {
      total: number
      current_page: number
      last_page: number
    }
  }> {
    return http(hemocultureEndpoints.reports(params))
  },

  async createReport(data: ReportFormData): Promise<HemocultureReport> {
    return http(hemocultureEndpoints.reports(), {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async updateReport(id: number, data: Partial<ReportFormData>): Promise<HemocultureReport> {
    return http(hemocultureEndpoints.report(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async deleteReport(id: number): Promise<void> {
    return http(hemocultureEndpoints.report(id), {
      method: 'DELETE',
    })
  },

  async submitReport(id: number): Promise<HemocultureReport> {
    return http(hemocultureEndpoints.reportSubmit(id), {
      method: 'POST',
    })
  },

  async validateReport(id: number): Promise<HemocultureReport> {
    return http(hemocultureEndpoints.reportValidate(id), {
      method: 'POST',
    })
  },

  async rejectReport(id: number, commentaire: string): Promise<HemocultureReport> {
    return http(hemocultureEndpoints.reportReject(id), {
      method: 'POST',
      body: JSON.stringify({
        commentaire,
      }),
    })
  },

  /**
   * Dashboard annuel
   */
  async getDashboard(params?: Record<string, string | number>): Promise<DashboardData> {
    return http(hemocultureEndpoints.dashboard(params))
  },

  /**
   * Evolution mensuelle.
   *
   * Retour attendu :
   *
   * {
   *   annee: 2026,
   *   evolution: [
   *     {
   *       mois: 1,
   *       type_patient: "Enfants",
   *       demandes: 800,
   *       echantillons_un_flacon: 743,
   *       flacons: 800,
   *       positifs: 217,
   *       ...
   *     },
   *     {
   *       mois: 1,
   *       type_patient: "Adultes",
   *       demandes: 445,
   *       echantillons_un_flacon: 390,
   *       flacons: 522,
   *       positifs: 130,
   *       ...
   *     }
   *   ]
   * }
   */
  async getEvolution(params?: Record<string, string | number>): Promise<EvolutionData> {
    return http(hemocultureEndpoints.dashboardEvolution(params))
  },

  async getComparatif(params?: Record<string, string | number>): Promise<unknown> {
    return http(hemocultureEndpoints.dashboardComparatif(params))
  },

  async getStatutSoumissions(params?: Record<string, string | number>): Promise<unknown> {
    return http(hemocultureEndpoints.dashboardStatutSoumissions(params))
  },
}
